import type { DevicePackageAssignmentCsvRow } from '../types/EventsOverviewTypes';

/** Shape returned by listRaceAthletes (DynamoDB operational state). */
export type RaceAthleteApiRow = {
  athleteId: string;
  athleteName?: string;
  athleteRaceNo?: string;
  assignedRearDeviceId?: string;
  assignedFrontDeviceId?: string;
  dns?: number;
  [key: string]: unknown;
};

export function hasAssignedDeviceValue(raw: unknown): boolean {
  const value = String(raw ?? '').trim();
  if (!value) return false;
  const normalized = value.toLowerCase();
  return normalized !== '-' && normalized !== '—' && normalized !== 'n/a' && normalized !== 'na';
}

export function rowHasAssignedDevice(row: DevicePackageAssignmentCsvRow): boolean {
  return hasAssignedDeviceValue(row.rearDeviceId) || hasAssignedDeviceValue(row.frontDeviceId);
}

export function raceAthleteToPackageAssignmentRow(
  athlete: RaceAthleteApiRow,
): DevicePackageAssignmentCsvRow {
  return {
    athleteId: String(athlete.athleteId ?? '').trim(),
    athleteName: String(athlete.athleteName ?? '').trim(),
    athleteRaceNo: String(athlete.athleteRaceNo ?? '').trim(),
    rearDeviceId: String(athlete.assignedRearDeviceId ?? '').trim(),
    frontDeviceId: String(athlete.assignedFrontDeviceId ?? '').trim(),
    verificationEditedAt: String(athlete.verificationEditedAt ?? '').trim(),
    verificationEditedStage: String(athlete.verificationEditedStage ?? '').trim(),
    verificationRearFrom: String(athlete.verificationRearFrom ?? '').trim(),
    verificationRearTo: String(athlete.verificationRearTo ?? '').trim(),
    verificationRearHistory: String(athlete.verificationRearHistory ?? '').trim(),
    verificationFrontFrom: String(athlete.verificationFrontFrom ?? '').trim(),
    verificationFrontTo: String(athlete.verificationFrontTo ?? '').trim(),
    verificationFrontHistory: String(athlete.verificationFrontHistory ?? '').trim(),
  };
}

export function raceAthletesToPackageAssignmentRows(
  athletes: RaceAthleteApiRow[],
  options: { includeDns?: boolean } = {},
): DevicePackageAssignmentCsvRow[] {
  return athletes
    .filter((a) => options.includeDns || Number(a.dns ?? 0) !== 1)
    .map(raceAthleteToPackageAssignmentRow)
    .filter((row) => String(row.athleteId ?? '').trim());
}

export function filterRackableAssignmentRows(
  rows: DevicePackageAssignmentCsvRow[],
): DevicePackageAssignmentCsvRow[] {
  return rows.filter(rowHasAssignedDevice);
}

export function parseRaceAthletesApiResponse(data: unknown): RaceAthleteApiRow[] {
  if (!data || typeof data !== 'object') return [];
  const athletes = (data as { athletes?: unknown }).athletes;
  if (!Array.isArray(athletes)) return [];
  return athletes.filter((a): a is RaceAthleteApiRow => {
    if (!a || typeof a !== 'object') return false;
    return Boolean(String((a as RaceAthleteApiRow).athleteId ?? '').trim());
  });
}
